import type { Content, CustomTableLayout, TDocumentDefinitions } from 'pdfmake/interfaces.js';
import { formatDay } from '../../format.js';
import { pdfBrandLogo } from '../../pdf-brand.js';
import { TOKENS } from '../../tokens.js';
import { monthLabel } from '../payroll/view.js';
import type { CashBalanceRow, CashReconciliation, CashTxn } from './types.js';
import { formatMoney, localTimeOf } from './view.js';

/** Çevirici: i18next `t`. */
export type CashT = (key: string, options?: Record<string, unknown>) => string;

export interface MonthlyStatementInput {
  vessel: { name: string };
  brand: { name: string; logo: string | null } | null;
  currency: string;
  month: string;
  summary: CashBalanceRow;
  /** Ayın işlemleri, defter sırasıyla (eskiden yeniye). */
  rows: readonly CashTxn[];
  reconciliation: CashReconciliation | null;
  printedAt: string;
  language: string;
}

const LINES: CustomTableLayout = {
  hLineWidth: (i) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  fillColor: (row) => (row === 0 ? TOKENS['table-head-bg'] : null),
};

/**
 * Aylık kasa dökümü (kasa.pdf §3, §4; ekran 6): A4 dikey; özet (açılış, giriş, çıkış, hesaplanan
 * kapanış, sayılan, fark), ayın işlemleri yürüyen bakiyeyle ve kaptan/ofis imza kutuları.
 */
export function monthlyStatementPdf(t: CashT, input: MonthlyStatementInput): TDocumentDefinitions {
  const money = (minor: number) => formatMoney(minor, input.language);
  const s = (key: string) => t(`cash.statement.${key}`);
  const rec = input.reconciliation;
  const logo = pdfBrandLogo(input.brand, { fit: [80, 40], width: 90 });
  const title = t('cash.panels.statement', {
    currency: input.currency,
    month: monthLabel(input.month, input.language),
  });
  const summary: [string, string][] = [
    [s('opening'), money(input.summary.opening)],
    [s('received'), money(input.summary.received)],
    [s('paid'), money(input.summary.paid)],
    [s('closing'), money(input.summary.opening + input.summary.received - input.summary.paid)],
    [s('counted'), rec ? money(rec.counted) : '—'],
    [s('difference'), rec ? money(rec.difference) : '—'],
  ];
  const body: Content[] = [
    {
      columns: [
        ...(logo ? [logo] : []),
        {
          stack: [
            { text: input.brand?.name ?? '', bold: true, fontSize: 11 },
            { text: input.vessel.name, fontSize: 10 },
          ],
        },
        {
          text: t('cash.statement.printed', { at: input.printedAt.slice(0, 16).replace('T', ' ') }),
          alignment: 'right',
          fontSize: 8,
          color: TOKENS['text-secondary'],
        },
      ],
    },
    { text: title, bold: true, fontSize: 14, margin: [0, 12, 0, 8] },
    {
      table: {
        widths: ['*', 110],
        body: summary.map(([label, value]) => [
          { text: label },
          { text: value, alignment: 'right', bold: true },
        ]),
      },
      layout: { ...LINES, fillColor: () => null },
      margin: [0, 0, 0, 12],
    },
    { text: s('ledger'), bold: true, fontSize: 11, margin: [0, 0, 0, 4] },
    {
      table: {
        headerRows: 1,
        widths: [52, 30, 46, '*', 58, 58, 62],
        body: [
          [
            t('cash.txn.columns.dateTime'),
            '',
            t('cash.txn.columns.id'),
            t('cash.txn.columns.description'),
            t('cash.balance.in'),
            t('cash.balance.out'),
            t('cash.balance.balance'),
          ].map((text, i) => ({ text, bold: true, alignment: i >= 4 ? 'right' : 'left' })),
          ...input.rows.map((r) => [
            formatDay(r.day),
            localTimeOf(r),
            r.txnNo,
            [
              t(`cash.txn.types.${r.txnType}`),
              r.category ? t(`cash.txn.categories.${r.category}`) : null,
              r.payee,
              r.remarks,
            ]
              .filter(Boolean)
              .join(' · '),
            { text: r.direction === 'in' ? money(r.amount) : '', alignment: 'right' as const },
            { text: r.direction === 'out' ? money(r.amount) : '', alignment: 'right' as const },
            { text: money(r.balance), alignment: 'right' as const },
          ]),
        ],
      },
      layout: LINES,
    },
    {
      columns: [
        {
          stack: [
            { text: s('masterApproval'), bold: true },
            { text: rec ? `${rec.signedBy} · ${formatDay(rec.signedAt.slice(0, 10))}` : ' ' },
            { canvas: [{ type: 'line', x1: 0, y1: 28, x2: 200, y2: 28, lineWidth: 0.5 }] },
          ],
        },
        {
          stack: [
            { text: s('officeApproval'), bold: true },
            {
              text: rec?.review
                ? `${t(`cash.reconcile.review.${rec.review.decision}`)} · ${formatDay(rec.review.at.slice(0, 10))}`
                : ' ',
            },
            { canvas: [{ type: 'line', x1: 0, y1: 28, x2: 200, y2: 28, lineWidth: 0.5 }] },
          ],
        },
      ],
      margin: [0, 24, 0, 0],
    },
  ];
  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [36, 36, 36, 48],
    defaultStyle: { fontSize: 8.5 },
    info: { title },
    content: body,
    footer: (page, pages) => ({
      text: `Felixmate · ${page} / ${pages}`,
      alignment: 'center',
      fontSize: 7,
      color: TOKENS['text-secondary'],
    }),
  };
}
